import { ReactElement, useState, useMemo } from 'react';
import { Stack } from '@mui/material';
import { ColumnDescription } from 'react-bootstrap-table-next';

import { useUtcOffset, useRequest, useTypePartnerCompany } from '~hooks/index';
import { getItemHistory } from '~services/item';
import { ItemHistory } from '~services/item/types';
import {
  AccessFeature,
  DataTable,
  OrderHistoryEventInfo,
  UserInfo,
} from '~components/index';
import NoRecordsFoundMessage from '~components/DataTable/NoRecordsFoundMessage';
import { PlanFeaturesTypes } from '~globals/types/enums';

import { useDialogDetailsOrderContextContext } from '../../DialogDetailsOrderContext';

const DialogDetailsOrderHistory = (): ReactElement => {
  const { orderId } = useDialogDetailsOrderContextContext();
  const formatterDateUtcOffset = useUtcOffset();
  const { isGiver } = useTypePartnerCompany();

  const [data, setData] = useState<ItemHistory[]>([]);

  const [, loading] = useRequest(
    {
      request: getItemHistory,
      payload: orderId,
      withPostSuccess: (response) => {
        const responseData = response.data?.data.results as ItemHistory[];

        setData(responseData);
      },
    },
    [],
  );

  const columns = useMemo<ColumnDescription<ItemHistory>[]>(
    () => [
      {
        dataField: 'creationDateTime',
        text: 'Fecha / Hora',
        classes: 'truncated-two-lines vertical-align-top',
        formatter: (cell) => (
          <span>{formatterDateUtcOffset(cell, 'DD/MM/YYYY HH:mm:ss')}</span>
        ),
        headerStyle: { width: 136 },
      },
      {
        dataField: 'description',
        isDummyField: true,
        text: 'Evento',
        classes: 'not-truncated',
        formatter: (_cell, row) => (
          <OrderHistoryEventInfo
            title={row.itemStoryType.description}
            description={row.description}
          />
        ),
      },
      {
        hidden: isGiver,
        dataField: 'driver',
        isDummyField: true,
        text: 'Asignación',
        classes: 'vertical-align-top',
        formatter: (_cell, row) => {
          if (!row.driver) return 'N/A';

          return (
            <UserInfo
              name={`${row.driver.lastName}, ${row.driver.firstName}`}
              urlAvatar={row.driver.avatarUrl || undefined}
              fontSize={13}
            />
          );
        },
        headerStyle: { width: 220 },
      },
      {
        hidden: isGiver,
        dataField: 'user',
        isDummyField: true,
        text: 'Responsable',
        classes: 'vertical-align-top',
        formatter: (_cell, row) => (
          <UserInfo
            name={`${row.user.lastName}, ${row.user.firstName}`}
            urlAvatar={row.user.avatarUrl || undefined}
            fontSize={13}
          />
        ),
        headerStyle: { width: 220 },
      },
    ],
    [formatterDateUtcOffset, isGiver],
  );

  return (
    <Stack spacing={2} height="100%">
      <AccessFeature.Alert validFeature={PlanFeaturesTypes.LimitPaid} />

      <AccessFeature.Hidden
        validFeature={PlanFeaturesTypes.LimitPaid}
        type="notAccess"
      >
        <DataTable
          loading={loading}
          data={data}
          columns={columns}
          keyField="itemStoryId"
          noDataIndicationComponent={() => (
            <NoRecordsFoundMessage description="" />
          )}
        />
      </AccessFeature.Hidden>
    </Stack>
  );
};

export default DialogDetailsOrderHistory;
